import React from 'react'
import { useTranslation } from 'react-i18next'

import Tooltip from '@mui/material/Tooltip'
import Stack from '@mui/material/Stack'
import TableCell from '@mui/material/TableCell'
import Typography from '@mui/material/Typography'
import CircleIcon from '@mui/icons-material/Circle'

import { toDateString, toReadableTimestamp } from '../../utils/dateUtils'
import ProfileTypeChip from './profileTypeChip'

import ResponsiveTableHeader from '../fields/responsiveTableHeader'
import { Profile } from '../../types/profile'
import { Customer } from '../../types/customer'
import { weightValueToDisplay } from '../../utils/weightUtils'
import { Checkbox, useMediaQuery, useTheme } from '@mui/material'

interface ProfileRowProps {
  profile: Profile
  currentCustomer: Customer | null
  isAssigned: boolean
  isBBP: boolean
  selectedProfiles: Profile[] | null
  assignedMachineSlotNumber: number | string
  downloadedTimestamp: Date | null | undefined
  canBeAssignedToMachine: boolean
  profileLink: JSX.Element
  createdBy: string
  handleProfileSelection: (profile: Profile) => void
}

const ProfileRow = (props: ProfileRowProps): JSX.Element => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isScreenBelowMdBreakpoint = useMediaQuery(theme.breakpoints.down('md'))

  return (
    <>
      {isScreenBelowMdBreakpoint && (
        <Stack direction={'row-reverse'} sx={{ length: '100%', height: 0 }}>
          <Checkbox
            sx={{ top: 10, right: 10 }}
            checked={props.selectedProfiles?.includes(props.profile)}
            onClick={() => {
              props.handleProfileSelection(props.profile)
            }}
          />
        </Stack>
      )}
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {!!props.assignedMachineSlotNumber && (
          <Stack direction="row" alignItems="center">
            <ResponsiveTableHeader header={t('profilesview.machineslot')} />
            <Typography>{props.assignedMachineSlotNumber}</Typography>
            <Tooltip
              sx={{ ml: 1 }}
              title={
                props.downloadedTimestamp
                  ? t('profilesview.downloadedtoroaster').replace('{0}', toReadableTimestamp(props.downloadedTimestamp))
                  : t('profilesview.notdownloaded')
              }
            >
              <CircleIcon fontSize="small" color={props.downloadedTimestamp ? 'success' : 'error'} />
            </Tooltip>
          </Stack>
        )}
        {props.isAssigned && props.isBBP && (
          <Tooltip title={t('profilesview.bbp')}>
            <Typography>{t('editprofileview.bbppagetitle')}</Typography>
          </Tooltip>
        )}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.name')} />
        {props.profileLink}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.createdby')} />
        {props.createdBy}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.batchweight')} />
        {props.profile.batch_weight ? weightValueToDisplay(props.profile.batch_weight) : ''}
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.profiletype')} />
        <ProfileTypeChip profile={props.profile} />
      </TableCell>
      <TableCell>
        <ResponsiveTableHeader header={t('profilesview.lastmodified')} />
        {toDateString(props.profile.modified)}
      </TableCell>
      <TableCell className="splitter-cell" />
      {!isScreenBelowMdBreakpoint && (
        <TableCell className={'action-cell'}>
          <Checkbox
            checked={props.selectedProfiles?.includes(props.profile)}
            onClick={() => {
              props.handleProfileSelection(props.profile)
            }}
          />
        </TableCell>
      )}
    </>
  )
}

export default ProfileRow
